import React from 'react';
import axios from 'axios';
import { Pill } from '../../../shared/Pill';
import { LOGIN_STATUS } from '../../crm/Leads/loginStatus';

// The Login Switcher (issue #283): one Customer's logins to view the portal
// as, used by the Login Switcher screen and the Customer record's View as
// Customer. A login that's switched off or paused shows why it can't be
// switched into; a Customer with no login at all can be switched into as
// "<Customer> — no login". Backend: services/loginSwitchService.js.

// Opens the portal as { customerId, loginId } (null loginId = the Customer
// itself) in a new tab. The tab is opened before the request so the
// browser treats it as part of the click rather than blocking it as a
// pop-up; it's closed again if the switch can't start.
export async function startLoginSwitch(customerId, loginId) {
  const tab = window.open('', '_blank');
  try {
    const res = await axios.post('/api/login-switcher/switches', { commercial_entity_id: customerId, customer_login_id: loginId });
    // The link also carries this page's address, so ending the switch can
    // take the tab back here.
    const url = `${res.data.data.url}&back=${encodeURIComponent(window.location.pathname + window.location.search)}`;
    if (tab) {
      tab.opener = null;
      tab.location.href = url;
    } else {
      window.open(url, '_blank', 'noopener');
    }
  } catch (err) {
    if (tab) tab.close();
    throw err;
  }
}

const rowStyle = { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', padding: '8px 0', borderTop: '1px solid #eef0f3' };
const viewButton = (enabled) => ({
  padding: '5px 12px', border: '1px solid #1976d2', borderRadius: 6, fontSize: 13, fontFamily: 'inherit',
  background: '#fff', color: '#1976d2', cursor: enabled ? 'pointer' : 'not-allowed', opacity: enabled ? 1 : 0.5,
});

function ViewAsButton({ enabled, label, onClick, busy }) {
  return (
    <button type="button" onClick={onClick} disabled={!enabled || busy} title={`Open the customer portal in a new tab as ${label}`} style={viewButton(enabled && !busy)}>
      View as
    </button>
  );
}

function LoginSwitchTargets({ customer, busy, onSwitch }) {
  if (customer.no_login) {
    const label = `${customer.name} — no login`;
    return (
      <div style={{ ...rowStyle, borderTop: 'none' }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 13, fontWeight: 600 }}>{label}</div>
          {customer.no_login.reason && <div style={{ fontSize: 12, color: '#b91c1c' }}>Can't switch in: {customer.no_login.reason}</div>}
        </div>
        <ViewAsButton enabled={customer.no_login.can_switch} label={label} busy={busy} onClick={() => onSwitch(customer, null)} />
      </div>
    );
  }
  return (
    <div>
      {customer.logins.map((login, i) => {
        const meta = LOGIN_STATUS[login.status] || LOGIN_STATUS.switched_off;
        return (
          <div key={login.id} style={{ ...rowStyle, borderTop: i === 0 ? 'none' : rowStyle.borderTop }}>
            <div style={{ minWidth: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                <span style={{ fontSize: 13, fontWeight: 600, wordBreak: 'break-all' }}>{login.email}</span>
                <Pill color={meta.color} background={meta.background} title={meta.title || login.status_reason}>{meta.label}</Pill>
              </div>
              <div style={{ fontSize: 12, color: '#5a6b85' }}>{login.owner_name} ({login.is_generic ? 'Company' : 'Individual'})</div>
              {login.reason && <div style={{ fontSize: 12, color: '#b91c1c' }}>Can't switch in: {login.reason}</div>}
            </div>
            <ViewAsButton enabled={login.can_switch} label={login.email} busy={busy} onClick={() => onSwitch(customer, login.id)} />
          </div>
        );
      })}
    </div>
  );
}

export default LoginSwitchTargets;
